import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import './SlideshowModal.css';
import {formatRelativeTime,} from "../../utils/formatUtils";

const SlideshowModal = ({ isOpen, onClose, notes, currentNoteIndex = 0, jobName,showTaskName = true, taskName: taskNameProp }) => {
  const [currentIndex, setCurrentIndex] = useState(currentNoteIndex);
  const [direction, setDirection] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setCurrentIndex(currentNoteIndex);
  }, [currentNoteIndex]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          document.exitFullscreen();
        } else {
          onClose();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if (e.key === 'h' || e.key === 'H') {
        setShowControls(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, notes.length, isFullscreen]);

  // Auto-hide controls
  useEffect(() => {
    if (!isOpen) return;

    const resetTimer = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    resetTimer();

    const handleMouseMove = () => resetTimer();
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isOpen]);
  useEffect(() => {
  if (isOpen) {
    setCurrentIndex(currentNoteIndex);
    setDirection(''); 
  }
}, [isOpen, currentNoteIndex, notes]);
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev < notes.length - 1) {
            return prev + 1;
          } else {
            setIsPlaying(false); 
            return prev;
          }
        });
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, notes.length]);

  const togglePlay = () => setIsPlaying(!isPlaying);

  const handleNext = useCallback(() => {
    if (currentIndex < notes.length - 1) {
      setDirection('next');
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, notes.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setDirection('prev');
      setDirection('prev');
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const toggleFullscreen = useCallback(() => {
    const element = document.querySelector('.slideshow-modal-content');
    if (!document.fullscreenElement) {
      element.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Function to get user initials
  const getUserInitials = (userName) => {
    if (!userName) return '?';
    return userName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };


  // Function to strip HTML and get plain text for thumbnail preview
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (!isOpen) return null;

  const currentNote = notes[currentIndex];
  const previousNote = currentIndex > 0 ? notes[currentIndex - 1] : null;
  const nextNote = currentIndex < notes.length - 1 ? notes[currentIndex + 1] : null;

  return (
    <div className="slideshow-modal-overlay" onClick={onClose}>
      <div className="slideshow-modal-content" onClick={e => e.stopPropagation()}>
        {/* Top Controls Bar - PowerPoint style */}
        <div className={`slideshow-top-bar ${showControls ? 'visible' : 'hidden'}`}>
          <div className="slideshow-top-bar-left">
            <button 
              className="slideshow-btn" 
              onClick={togglePlay} 
              title={isPlaying ? 'Pause Auto-play' : 'Play All'}
            >
              <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
            </button>
            <button 
              className="slideshow-btn" 
              onClick={toggleFullscreen} 
              title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
            >
              <i className={`fas fa-${isFullscreen ? 'compress' : 'expand'}`} />
            </button>
            <span className="slideshow-title">
              <i className="fas fa-images" />
              {jobName || 'Notes'} - Slide {currentIndex + 1} of {notes.length}
            </span>
          </div>
          <div className="slideshow-top-bar-right">            
            <button className="slideshow-btn" onClick={onClose} title="Close (Esc)">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        <div className="slideshow-body">
      {previousNote && (
        <div className={`slideshow-preview-card prev ${showControls ? 'visible' : 'hidden'}`} onClick={handlePrevious}>
          <div className="slideshow-note is-preview">
            <div className="task-accent-bar" />
            <div className="slideshow-note-header">
              <div className="task-header-left">
                <i className="fas fa-chevron-left" style={{marginRight: '8px'}}></i>
                <span className="task-label">Previous</span>
              </div>
            </div>
            <div className="task-main-text">
              {stripHtml(previousNote?.note || previousNote?.content || '').substring(0, 80)}...
            </div>
            
            <div className="task-footer">
              <div className="slideshow-note-avatar">
                {getUserInitials(previousNote?.userName)}
              </div>
              <div className="slideshow-note-user-details">
                <span className="task-requested-by">
                  <strong>{previousNote?.userName || 'Unknown'}</strong>
                </span>
                <span className="slideshow-note-date">
                  <i className="far fa-clock"></i> {formatRelativeTime(previousNote?.date || previousNote?.timeStamp)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

     
      <div className="slideshow-note-container">
        <div className="slideshow-note">
          <div className="task-accent-bar" />
          <div className="slideshow-note-header">
            <div className="task-header-left">
        <i className="fas fa-clipboard-check task-icon"></i>
        <div className="task-breadcrumb-wrapper">
          
          <span className="breadcrumb-item" title="Workspace">
            {currentNote?.workspaceName || 'Workspace'}
          </span>
          <i className="fas fa-chevron-right breadcrumb-sep"></i>
          
          
          <span className="breadcrumb-item" title="Project">
            {currentNote?.projectName || 'Project'}
          </span>
          <i className="fas fa-chevron-right breadcrumb-sep"></i>
          
          
          <span className="breadcrumb-item" title="Job">
            {currentNote?.jobName || jobName || 'Job'}
          </span>
          <i className="fas fa-chevron-right breadcrumb-sep"></i>
          
         
          {showTaskName && (
            <>
              <span className="task-label active-breadcrumb">
                {currentNote?.taskName || 
                currentNote?.task?.title || 
                currentNote?.title || 
                ' '}
              </span>
            </>
          )}
        </div>
      </div>
      
    </div>
    <div className="slideshow-note-content">
      <h2 className="task-main-text">
        {stripHtml(currentNote?.note || currentNote?.content || 'No content')}
      </h2>
    </div>
    <div className="task-footer">
      <div className="slideshow-note-avatar">
        {currentNote?.userImageUrl ? (
          <img src={currentNote.userImageUrl} alt="User" />
        ) : (
          getUserInitials(currentNote?.userName)
        )}
      </div>
      <div className="slideshow-note-user-details">
        <span className="task-requested-by">
          <strong>{currentNote?.userName || 'Unknown'}</strong>
        </span>
        <span className="slideshow-note-date">
          <i className="far fa-clock"></i> {formatRelativeTime(currentNote?.date || currentNote?.timeStamp)}
        </span>
      </div>
    </div>
  </div>
</div>


{nextNote && (
  <div className={`slideshow-preview-card next ${showControls ? 'visible' : 'hidden'}`} onClick={handleNext}>
    <div className="slideshow-note is-preview">
      <div className="task-accent-bar" />
      <div className="slideshow-note-header">
        <div className="task-header-right" style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end'}}>
          <span className="task-label" style={{marginRight: '8px'}}>Next</span>
          <i className="fas fa-chevron-right"></i>
        </div>
      </div>
      <div className="task-main-text">
        {stripHtml(nextNote?.note || nextNote?.content || '').substring(0, 80)}...
      </div>
      
      <div className="task-footer">
        <div className="slideshow-note-avatar">
          {getUserInitials(nextNote?.userName)}
        </div>
        <div className="slideshow-note-user-details">
          <span className="task-requested-by">
            <strong>{nextNote?.userName || 'Unknown'}</strong>
          </span>
          <span className="slideshow-note-date">
            <i className="far fa-clock"></i> {formatRelativeTime(nextNote?.date || nextNote?.timeStamp)}
          </span>
        </div>
      </div>
    </div>
  </div>
)}

          {/* Navigation arrows - PowerPoint style */}
          <button 
            className={`slideshow-nav prev ${showControls ? 'visible' : 'hidden'}`} 
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            title="Previous (←)"
          >
            <i className="fas fa-chevron-left" />
          </button>

          <button 
            className={`slideshow-nav next ${showControls ? 'visible' : 'hidden'}`} 
            onClick={handleNext}
            disabled={currentIndex === notes.length - 1}
            title="Next (→)"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>

        {/* Bottom thumbnails bar - PowerPoint style */}
        <div className={`slideshow-footer ${showControls ? 'visible' : 'hidden'}`}>
          <div className="slideshow-thumbnails-container">
            <button 
              className="thumbnails-nav prev" 
              onClick={() => {
                const container = document.querySelector('.slideshow-thumbnails');
                container.scrollBy({ left: -200, behavior: 'smooth' });
              }}
              title="Scroll left"
            >
              <i className="fas fa-chevron-left" />
            </button>
            
            <div className="slideshow-thumbnails">
              {notes.map((note, idx) => {
                const noteContent = note.note || note.content || '';
                return (
                  <div
                    key={note.id || idx}
                    className={`slideshow-thumbnail ${idx === currentIndex ? 'active' : ''}`}
                    onClick={() => setCurrentIndex(idx)}
                  >
                    <div className="thumbnail-number">{idx + 1}</div>
                    <div className="thumbnail-preview">
                      {stripHtml(noteContent).substring(0, 30)}...
                    </div>
                  </div>
                );
              })}
            </div>

            <button 
              className="thumbnails-nav next" 
              onClick={() => {
                const container = document.querySelector('.slideshow-thumbnails');
                container.scrollBy({ left: 200, behavior: 'smooth' });
              }}
              title="Scroll right"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>

        {/* Bottom progress bar - PowerPoint style */}
        <div className="slideshow-progress">
          <div 
            className="slideshow-progress-bar" 
            style={{ width: `${((currentIndex + 1) / notes.length) * 100}%` }}
          />
        </div>

        {/* Slide number indicator - bottom right */}
        <div className="slideshow-slide-number">
          {currentIndex + 1} / {notes.length}
        </div>
      </div>
    </div>
  );
};

SlideshowModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notes: PropTypes.array.isRequired,
  currentNoteIndex: PropTypes.number,
  jobName: PropTypes.string,
};

export default SlideshowModal;